/**
 * Recordatorios inteligentes (#4) — Modal de follow-up con IA.
 *
 * Se abre desde una notificación OPPORTUNITY_STALE para que el usuario
 * genere y revise un correo de seguimiento al cliente. Elige el tono,
 * pulsa "Generar", revisa el { subject, body } y lo copia o abre la
 * oportunidad.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AlertCircle, Copy, ExternalLink, Loader2, Sparkles } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'

type Tone = 'cordial' | 'directo' | 'consultivo'

const inputCls =
  'w-full px-2 py-1 rounded bg-[var(--bg-input-soft)] border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50'

interface Props {
  open: boolean
  onClose: () => void
  opportunityId: string
  opportunityTitle: string
}

interface DraftResponse {
  subject: string
  body: string
  conversationId: string
}

type Status = 'idle' | 'generating' | 'generated' | 'error'

export default function FollowupModal({
  open,
  onClose,
  opportunityId,
  opportunityTitle,
}: Props) {
  const { t } = useTranslation(['notifications', 'common'])
  const TONE_OPTIONS: { value: Tone; label: string }[] = [
    { value: 'cordial', label: t('followup.tone.cordial') },
    { value: 'directo', label: t('followup.tone.directo') },
    { value: 'consultivo', label: t('followup.tone.consultivo') },
  ]
  const [tone, setTone] = useState<Tone>('cordial')
  const [status, setStatus] = useState<Status>('idle')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [aiNotConfigured, setAiNotConfigured] = useState(false)
  const [copied, setCopied] = useState<'none' | 'subject' | 'body' | 'all'>('none')

  // Reset al abrir.
  useEffect(() => {
    if (!open) return
    setTone('cordial')
    setStatus('idle')
    setSubject('')
    setBody('')
    setError(null)
    setAiNotConfigured(false)
    setCopied('none')
  }, [open])

  const onGenerate = async () => {
    setStatus('generating')
    setError(null)
    setAiNotConfigured(false)
    setCopied('none')
    try {
      const res = await api.post<DraftResponse>('/ai/draft-followup', {
        opportunityId,
        tone,
      })
      setSubject(res.subject)
      setBody(res.body)
      setStatus('generated')
    } catch (err) {
      const apiBody =
        err instanceof ApiError ? (err.body as { code?: string; error?: string } | null) : null
      if (apiBody?.code === 'AI_NOT_CONFIGURED') {
        setAiNotConfigured(true)
        setStatus('error')
      } else {
        setError(
          apiBody?.error ?? (err instanceof Error ? err.message : t('followup.errors.generic'))
        )
        setStatus('error')
      }
    }
  }

  const copyAll = async () => {
    try {
      const full = `${t('followup.subjectPrefix')}: ${subject}\n\n${body}`
      await navigator.clipboard.writeText(full)
      setCopied('all')
      setTimeout(() => setCopied('none'), 1800)
    } catch {
      /* noop */
    }
  }

  const copyOnly = async (which: 'subject' | 'body') => {
    try {
      await navigator.clipboard.writeText(which === 'subject' ? subject : body)
      setCopied(which)
      setTimeout(() => setCopied('none'), 1800)
    } catch {
      /* noop */
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${t('followup.title')} — ${opportunityTitle}`}
      description={t('followup.description')}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Selector de tono + botón generar */}
        <div className="surface p-4 space-y-3">
          <label className="block space-y-1">
            <span className="text-xs t-secondary">{t('followup.toneLabel')}</span>
            <div className="flex flex-wrap gap-2">
              {TONE_OPTIONS.map((opt) => {
                const active = tone === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTone(opt.value)}
                    disabled={status === 'generating'}
                    className={
                      'px-2.5 py-1 rounded-lg text-xs border transition ' +
                      (active
                        ? 'border-toga-500/60 bg-toga-500/10 text-toga-800 dark:text-toga-100'
                        : 'border-base t-secondary hover:bg-hover')
                    }
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </label>

          <button
            type="button"
            onClick={() => void onGenerate()}
            disabled={status === 'generating'}
            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'generating' ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Sparkles size={13} />
            )}
            {status === 'generated' ? t('followup.regenerate') : t('followup.generate')}
          </button>

          {aiNotConfigured && (
            <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
              <AlertCircle size={12} />
              {t('followup.errors.aiNotConfigured')}
            </div>
          )}

          {error && !aiNotConfigured && (
            <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
              <AlertCircle size={12} /> {error}
            </div>
          )}
        </div>

        {/* Resultado editable */}
        {(status === 'generated' || (status === 'generating' && subject)) && (
          <div className="surface p-4 space-y-3">
            <label className="block space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs t-secondary">{t('followup.subjectLabel')}</span>
                <button
                  type="button"
                  onClick={() => void copyOnly('subject')}
                  className="text-[11px] t-faint hover:t-secondary flex items-center gap-1"
                >
                  <Copy size={11} />
                  {copied === 'subject' ? t('followup.copied') : t('followup.copy')}
                </button>
              </div>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className={inputCls}
              />
            </label>

            <label className="block space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs t-secondary">{t('followup.bodyLabel')}</span>
                <button
                  type="button"
                  onClick={() => void copyOnly('body')}
                  className="text-[11px] t-faint hover:t-secondary flex items-center gap-1"
                >
                  <Copy size={11} />
                  {copied === 'body' ? t('followup.copied') : t('followup.copy')}
                </button>
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className={`${inputCls} font-sans leading-relaxed`}
              />
            </label>
          </div>
        )}

        {/* Acciones */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to={`/opportunities/${opportunityId}`}
            onClick={onClose}
            className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
          >
            <ExternalLink size={13} /> {t('followup.openOpportunity')}
          </Link>
          {status === 'generated' && (
            <button
              type="button"
              onClick={() => void copyAll()}
              className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <Copy size={13} />
              {copied === 'all' ? t('followup.copied') : t('followup.copyToClipboard')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga"
          >
            {t('common:actions.close')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
